import * as vp from 'vopay';
import * as Yup from 'yup';
import { useState, useRef, FunctionComponent, useEffect } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import { Formik } from 'formik';
import styles from '../Login.module.scss';
import { Button } from 'UI';
import usePostClientResetPasswordEmail from '../../../hooks/useApi/usePostClientResetPasswordEmail';
import usePostVerifyRecaptcha from '../../../hooks/useApi/usePostVerifyRecaptcha';
import { useTranslation } from 'react-i18next';

const { TextField, Button: TextButton, Box, Title } = vp;

type ResetPasswordProps = {
  handleResetPassword: (e: boolean) => void;
  handleLoginPage: (e: boolean) => void;
};

type FC = FunctionComponent<ResetPasswordProps>;

// ResetPassword component — manage reCAPTCHA keys/configuration at https://www.google.com/recaptcha/admin
const ResetPassword: FC = (props: ResetPasswordProps) => {
  const { handleResetPassword, handleLoginPage } = props;

  const [, postResetPasswordEmail] = usePostClientResetPasswordEmail({});
  const [recaptchaResponse, postRecaptchaResponse] = usePostVerifyRecaptcha({});
  const [shouldShowValidateError, setShouldShowValidateError] = useState(false);
  const [isEmailSent, setIsEmailSent] = useState(false);
  const [isDisabled, setIsDisabled] = useState(false);
  const captchaRef = useRef<ReCAPTCHA>(null);
  const { t } = useTranslation('translations', { keyPrefix: 'Login.ResetPassword' });
  const { t: tGlobal } = useTranslation('translations', { keyPrefix: 'Global.Buttons' });

  const sendResetEmail = (value: vp.ClientResetPasswordEmailRequest) => {
    if (recaptchaResponse.Success) {
      setIsEmailSent(true);
      postResetPasswordEmail(value);
    } else {
      setShouldShowValidateError(true);
    }
  };

  const onChange = () => {
    setShouldShowValidateError(false);

    // reCAPTCHA also fires onChange with no token when one expires; there is
    // nothing to verify then, so leave the form enabled for another attempt.
    const response = captchaRef.current?.getValue();

    if (!response) return;

    setIsDisabled(true);
    postRecaptchaResponse({ response });
  };

  useEffect(() => {
    if (recaptchaResponse.Success) {
      setIsDisabled(false);
    }
  }, [recaptchaResponse.Success]);

  return (
    <>
      {isEmailSent ? (
        <Box flx="col">
          <Box mb="sm" flx="col" ai="center" height="auto">
            <Title title={t('Resume.title')} bold size="xsm" />
          </Box>

          <div className={styles.errorText}>{t('Resume.description')}</div>

          <Box jc="fe" flx="col" height="auto">
            <TextButton
              id="return-to-login-page"
              type="button"
              label={t('Resume.returnButton')}
              textSize={1}
              onClick={() => {
                setIsEmailSent(false);
                handleResetPassword(false);
                handleLoginPage(true);
              }}
            />
          </Box>
        </Box>
      ) : (
        <Box flx="col" width="100%">
          <Box mb="sm" flx="col" ai="center" height="auto">
            <Title title={t('Form.title')} bold size="xsm" />
          </Box>

          <Formik
            initialValues={{ Username: '' }}
            validationSchema={resetPasswordValidationSchema()}
            onSubmit={sendResetEmail}
          >
            {({ values, handleChange, handleBlur, handleSubmit, errors, touched }) => (
              <form onSubmit={handleSubmit}>
                <Box mb="sm" flx="col" height="auto">
                  <TextField
                    key="Username"
                    name="Username"
                    placeholder={t('Form.inputPlaceholder')}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.Username}
                    variant="border"
                    height="50px"
                    error={errors.Username && touched.Username ? errors.Username : null}
                  />
                </Box>

                <div className={styles.reCAPTCHAContainer}>
                  <ReCAPTCHA sitekey="6LeoUTQjAAAAACMy3_eh0jZav72yZ-l7zPgq24OM" onChange={onChange} ref={captchaRef} />
                  {shouldShowValidateError && <div className={styles.errorMSG}>{t('Form.notARobotValidation')}</div>}
                </div>

                <Box jc="fe" flx="col" height="auto">
                  <Button
                    id="reset-password-submit"
                    type="submit"
                    colorScheme="primary"
                    size="large"
                    className={styles.submitButton}
                    disabled={isDisabled}
                  >
                    {t('Form.submitButton')}
                  </Button>
                </Box>
              </form>
            )}
          </Formik>

          <Box mt="sm" height="auto" jc="center" ai="center">
            <TextButton
              id="forgot-password-cancel"
              type="button"
              label={tGlobal('goBack', { defaultValue: 'Go back' })}
              color="none"
              noShadow
              noPadding
              bold
              textSize={1}
              onClick={() => {
                handleResetPassword(false);
                handleLoginPage(true);
              }}
            />
          </Box>
        </Box>
      )}
    </>
  );

  function resetPasswordValidationSchema() {
    return Yup.object().shape({
      Username: Yup.string().label(t('Form.usernameLabel')).required(),
    });
  }
};

export default ResetPassword;
